import * as React from 'react';

import { FormHelperText, Typography, Chip } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';

import { gray100, gray80, orange50, red50 } from '../../../constants/colors';
import { formLabelWrapper, textMb, titleMb } from '../../../constants/location';

import type { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form';

type radioProps = React.ComponentProps<typeof RadioGroup>;

type Props = radioProps & {
  labelText?: string;
  requiredText?: string;
  required: boolean;
  error?: boolean;
  helperText?:
    | string
    | FieldError
    | Merge<FieldError, FieldErrorsImpl<Record<string, unknown>>>
    | undefined;
  width?: string;
  firstOption?: string;
  secondOption?: string;
  firstOptionDescription?: string;
  secondOptionDescription?: string;
  firstValue?: string | number;
  secondValue?: string | number;
  disabled?: boolean;
  guideText?: string;
  disabledColor?: string;
  defaultValue?: string | number; // デフォルト値を表示するため
  attentionText?: string; // ラベル横に表示する注意書き
  optionFontSize?: string; // 選択肢ラベルのフォントサイズ（未指定時はデフォルト）
  inlineDescription?: boolean; // true で説明文をラベルの右に横並び表示（既定は下に縦並び）
  // 後方互換性のための旧props（deprecated）
  trueOption?: string;
  falseOption?: string;
  trueOptionDescription?: string;
  falseOptionDescription?: string;
};

const styles = {
  formLabelWrapper,
  guideCard: {
    '@media screen and (max-width:600px)': {
      width: '80%',
    },
    width: '480px',
  },
  guideMark: {
    color: gray80,
    cursor: 'pointer',
    height: '18px',
    marginLeft: '4px',
    verticalAlign: 'middle',
    width: '18px',
  },
  guideText: {
    color: gray80,
    fontSize: '12px',
    textAlign: 'left',
  },
  guideTitle: {
    color: gray100,
    fontSize: '22px',
    marginBottom: titleMb,
    padding: 0,
  },
  helper: {
    '@media screen and (max-width:480px)': {
      marginLeft: 0,
    },
    marginLeft: 0,
  },
  label: {
    fontSize: '14px',
  },
  required: {
    color: red50,
    fontSize: '12px',
    marginLeft: '10px',
  },
  optionDescription: {
    fontSize: '12px',
    color: gray80,
  },
  radioLabelContainer: {
    height: '42px',
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px',
  },
  radio: {
    width: '42px',
    height: '42px',
    '&.Mui-checked': {
      color: orange50,
    },
  },
  labelText: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '8px',
  },
  defaultChip: {
    fontSize: '9px',
    height: '20px',
    marginLeft: '8px',
    backgroundColor: 'transparent',
    borderColor: gray80,
    color: gray80,
    borderRadius: '4px',
    '& .MuiChip-label': {
      fontSize: '10px',
      padding: '3px 6px',
    },
  },
  attentionText: {
    color: red50,
    fontSize: '12px',
    marginLeft: '8px',
  },
};

const CustomRedioButton = React.forwardRef((props: Props, _ref): React.ReactElement => {
  const {
    labelText,
    requiredText,
    required,
    error,
    helperText,
    firstOption,
    secondOption,
    firstOptionDescription,
    secondOptionDescription,
    firstValue = 'true',
    secondValue = 'false',
    disabled,
    disabledColor,
    guideText,
    defaultValue,
    attentionText,
    optionFontSize,
    inlineDescription,
    // 後方互換性のための旧props
    trueOption,
    falseOption,
    trueOptionDescription,
    falseOptionDescription,
    ...rest
  } = props;

  // 後方互換性のため、新props優先で旧propsをフォールバック
  const option1 = firstOption || trueOption;
  const option2 = secondOption || falseOption;
  const option1Description = firstOptionDescription || trueOptionDescription;
  const option2Description = secondOptionDescription || falseOptionDescription;

  const textfieldHelper = (): string | undefined => {
    if (helperText === undefined) return;
    return String(helperText);
  };

  return (
    <Typography sx={textMb} align="center" component="div">
      <Typography
        align="left"
        sx={[styles.formLabelWrapper, inlineDescription ? { marginBottom: '2px' } : {}]}
        component="div"
      >
        <Typography color={gray80} sx={styles.label} component="span">
          {labelText}
        </Typography>
        {required ? (
          <Typography component="span" sx={styles.required}>
            {requiredText}
          </Typography>
        ) : (
          ''
        )}
        {attentionText && (
          <Typography component="span" sx={styles.attentionText}>
            {attentionText}
          </Typography>
        )}
      </Typography>
      {guideText && (
        <div style={{ marginBottom: '12px', textAlign: 'left' }}>
          {guideText.split(/\r?\n/).map((line, index) => (
            <Typography key={index} sx={styles.guideText} component="div" align="left">
              {line}
            </Typography>
          ))}
        </div>
      )}
      <Typography align="left" component="div">
        <FormControl>
          <RadioGroup
            aria-labelledby="demo-form-control-label-placement"
            name="position"
            sx={{
              '& .Mui-disabled': {
                WebkitTextFillColor: disabledColor ?? 'initial',
              },
              '&.MuiFormGroup-root': { alignContent: 'flex-start' },
            }}
            {...rest}
          >
            <FormControlLabel
              value={firstValue}
              control={<Radio sx={styles.radio} />}
              label={
                <Typography
                  sx={[
                    styles.labelText,
                    optionFontSize ? { fontSize: optionFontSize } : {},
                    inlineDescription
                      ? { flexDirection: 'row', alignItems: 'baseline', gap: '12px' }
                      : {},
                  ]}
                  component="span"
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span>{option1}</span>
                    {defaultValue && String(defaultValue) === String(firstValue) && (
                      <Chip
                        label="デフォルト"
                        size="small"
                        variant="outlined"
                        sx={styles.defaultChip}
                      />
                    )}
                  </div>
                  {option1Description && (
                    <Typography sx={styles.optionDescription} component="span">
                      {option1Description}
                    </Typography>
                  )}
                </Typography>
              }
              sx={{
                color: gray100,
                '& .Mui-disabled': {
                  WebkitTextFillColor: disabledColor ?? 'initial',
                },
                '.MuiSvgIcon-root': {
                  color: disabled && disabledColor ? disabledColor : 'inherit',
                },
                ...styles.radioLabelContainer,
                // 横並び時は1行で収まるため、行間（縦の隙間）を詰める
                ...(inlineDescription && { marginBottom: '0px' }),
              }}
              {...(disabled && { disabled })}
            />
            <FormControlLabel
              value={secondValue}
              control={<Radio sx={styles.radio} />}
              label={
                <Typography
                  sx={[
                    styles.labelText,
                    optionFontSize ? { fontSize: optionFontSize } : {},
                    inlineDescription
                      ? { flexDirection: 'row', alignItems: 'baseline', gap: '12px' }
                      : {},
                  ]}
                  component="span"
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span>{option2}</span>
                    {defaultValue && String(defaultValue) === String(secondValue) && (
                      <Chip
                        label="デフォルト"
                        size="small"
                        variant="outlined"
                        sx={styles.defaultChip}
                      />
                    )}
                  </div>
                  {option2Description && (
                    <Typography sx={styles.optionDescription} component="span">
                      {option2Description}
                    </Typography>
                  )}
                </Typography>
              }
              sx={{
                color: gray100,
                '& .Mui-disabled': {
                  WebkitTextFillColor: disabledColor ?? 'initial',
                },
                '.MuiSvgIcon-root': {
                  color: disabled && disabledColor ? disabledColor : 'inherit',
                },
                ...styles.radioLabelContainer,
                // 横並び時は1行で収まるため、行間（縦の隙間）を詰める
                ...(inlineDescription && { marginBottom: '0px' }),
              }}
              {...(disabled && { disabled })}
            />
          </RadioGroup>
        </FormControl>
      </Typography>
      <FormHelperText sx={styles.helper} error={error}>
        {textfieldHelper()}
      </FormHelperText>
    </Typography>
  );
});
CustomRedioButton.displayName = 'CustomRedioButton';
export default CustomRedioButton;
